import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import HdIcon from "@mui/icons-material/Hd";
import HdOutlinedIcon from "@mui/icons-material/HdOutlined";
import HighlightAltIcon from "@mui/icons-material/HighlightAlt";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { api, unwrap } from "../api/client";
import { fileAt } from "../playback/plan";
import type { FullState, SessionSource } from "../playback/session";
import {
  DropWatch,
  fill,
  LAYOUTS,
  place,
  relief,
  type Layout,
  type Slots,
} from "./grid";
import { UI_INTERVALS_MS } from "./uiFrames";
import { boxesAt, contained } from "./motionBoxes";
import { COARSE } from "../theme";

const LAYOUT_KEY = "avgv.gridLayout";
const MAX_ZOOM = 8;
const WATCH_EVERY_MS = 500;
/** After slowing the page or pausing a slot, playback gets this long to recover before the next step. */
const PAUSE_COOLDOWN_MS = 5000;
/**
 * Drops are ignored this long after playback starts or resumes: the Android
 * tablet drops ~30% of frames for the first seconds, even with one video.
 */
const WARMUP_MS = 5000;
/** Dropped shares that slow the page's updates, and then pause a slot. */
const DROP_LIMITS = { slowUi: 0.1, pause: 0.2 };

function storedLayout(): Layout | null {
  try {
    const v = localStorage.getItem(LAYOUT_KEY);
    return v && v in LAYOUTS ? (v as Layout) : null;
  } catch {
    return null;
  }
}

/** The smallest layout that shows every source, up to 2 × 2. */
function fitting(count: number): Layout {
  return count <= 1 ? "1x1" : count === 2 ? "1x2" : "2x2";
}

const utcTime = (ms: number) => new Date(ms).toISOString().slice(11, 21);
const localTime = (ms: number) =>
  new Date(ms).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });

/**
 * The video sources in a 1×1, 1×2, 2×2 or 1 large + 3 layout (design §7.3).
 * Sources are swapped between slots by dragging or with each slot's picker;
 * any slot can be maximised. Only the videos on screen are decoded, and when
 * the device can't keep up, the page updates less often and then the
 * lowest-priority slots are paused, unless the user chooses to show all. A maximised slot can play at full resolution.
 */
export function VideoGrid({
  sources,
  active,
  now,
  playing,
  onShow,
  controls,
  full,
  onFull,
  uiStep,
  onUiStep,
  panels = [],
}: {
  /** Shown in a slot instead of a video when chosen there: the spectrogram, the map. */
  panels?: readonly { key: string; title: string; node: React.ReactNode }[];
  sources: readonly SessionSource[];
  active: ReadonlySet<string>;
  now: number;
  playing: boolean;
  /** The sources whose video should be decoded. */
  onShow: (keys: ReadonlySet<string>) => void;
  controls: (s: SessionSource) => React.ReactNode;
  full: FullState | null;
  onFull: (key: string | null) => void;
  /** Index into UI_INTERVALS_MS: how far the page's updates have been slowed. */
  uiStep: number;
  onUiStep: (step: number) => void;
}) {
  const [chosen, setChosen] = useState<Layout | null>(storedLayout);
  const layout = chosen ?? fitting(sources.length);
  const [assigned, setAssigned] = useState<Slots>([]);
  const [maximised, setMaximised] = useState<number | null>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const keys = sources.map((s) => s.plan.key);
  const slots = fill(
    assigned,
    keys,
    LAYOUTS[layout].slots,
    panels.map((p) => p.key),
  );
  const panelOf = (key: string | null) => panels.find((p) => p.key === key);
  const byKey = new Map(sources.map((s) => [s.plan.key, s]));

  const chooseLayout = (l: Layout) => {
    setChosen(l);
    setMaximised(null);
    try {
      localStorage.setItem(LAYOUT_KEY, l);
    } catch {
      // Remembering the layout is a convenience.
    }
  };
  const show = (i: number, key: string | null) =>
    setAssigned(place(slots, i, key));

  const shown =
    maximised === null
      ? slots.map((k, i) => [k, i] as const)
      : [[slots[maximised] ?? null, maximised] as const];
  const big = layout === "1+3" && maximised === null;
  const maxKey = maximised === null ? null : (slots[maximised] ?? null);
  const fullKey = full?.key ?? null;
  useEffect(() => {
    if (fullKey !== null && fullKey !== maxKey) onFull(null);
  }, [fullKey, maxKey, onFull]);

  const [paused, setPaused] = useState<ReadonlySet<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  const live = shown.map(([k]) =>
    k !== null && (showAll || !paused.has(k)) ? k : null,
  );
  const liveKey = live.join("\n");
  useEffect(() => {
    onShow(new Set(liveKey.split("\n").filter(Boolean)));
  }, [liveKey, onShow]);

  const liveCount = live.filter(Boolean).length;
  useEffect(() => {
    if (liveCount < 2) onUiStep(0);
  }, [liveCount, onUiStep]);
  const step = useRef(uiStep);
  useEffect(() => {
    step.current = uiStep;
  }, [uiStep]);

  const watch = useRef(new DropWatch());
  const lastPause = useRef(-Infinity);
  const chains = useRef(byKey);
  useEffect(() => {
    chains.current = byKey;
  });
  useEffect(() => {
    watch.current.clear();
    if (!playing || showAll) return;
    const keys = liveKey.split("\n").map((k) => k || null);
    let steadySince = performance.now();
    const timer = setInterval(() => {
      const t = performance.now();
      const videos = keys.flatMap((k) => {
        const v = k ? chains.current.get(k)?.chain?.video : undefined;
        return k && v ? [[k, v] as const] : [];
      });
      if (videos.some(([, v]) => v.paused || v.seeking)) {
        watch.current.clear();
        steadySince = t;
        return;
      }
      if (t - steadySince < WARMUP_MS) return;
      for (const [k, v] of videos) {
        const q = v.getVideoPlaybackQuality();
        watch.current.add(k, t, q.droppedVideoFrames, q.totalVideoFrames);
      }
      if (t - lastPause.current < PAUSE_COOLDOWN_MS) return;
      const action = relief(
        keys,
        (k) => watch.current.ratio(k),
        step.current < UI_INTERVALS_MS.length - 1,
        DROP_LIMITS,
      );
      if (action === "slow-ui") {
        lastPause.current = t;
        watch.current.clear();
        onUiStep(step.current + 1);
        return;
      }
      const key = action && keys[action.pause];
      if (!key) return;
      lastPause.current = t;
      setPaused((p) => new Set([...p, key]));
    }, WATCH_EVERY_MS);
    return () => clearInterval(timer);
  }, [playing, showAll, liveKey, onUiStep]);

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: "center", gap: 1, mb: 1 }}>
        <Select
          size="small"
          value={layout}
          onChange={(e) => chooseLayout(e.target.value as Layout)}
          inputProps={{ "aria-label": "Layout" }}
        >
          {(Object.keys(LAYOUTS) as Layout[]).map((l) => (
            <MenuItem key={l} value={l}>
              {LAYOUTS[l].label}
            </MenuItem>
          ))}
        </Select>
        {!showAll && paused.size > 0 && (
          <>
            <Chip
              size="small"
              color="warning"
              label={`${paused.size} paused for performance`}
            />
            <Button size="small" onClick={() => setShowAll(true)}>
              Show all
            </Button>
          </>
        )}
        {showAll && (
          <>
            <Chip
              size="small"
              color="warning"
              label="All shown: sync accuracy reduced"
            />
            <Button
              size="small"
              onClick={() => {
                setShowAll(false);
                setPaused(new Set());
              }}
            >
              Adapt to this device
            </Button>
          </>
        )}
        {sources.length > slots.filter(Boolean).length && (
          <Typography variant="caption" color="text.secondary">
            {sources.length - slots.filter(Boolean).length} more not shown:
            choose them in a slot
          </Typography>
        )}
      </Stack>
      <Box
        sx={{
          display: "grid",
          gap: 1,
          gridTemplateColumns:
            maximised !== null || layout === "1x1"
              ? "1fr"
              : big
                ? "3fr 1fr"
                : "repeat(2, minmax(0, 1fr))",
          gridTemplateRows: big ? "repeat(3, auto)" : undefined,
        }}
      >
        {shown.map(([key, i]) => {
          const source = key === null ? undefined : byKey.get(key);
          const picker = (
            <Select
              size="small"
              variant="standard"
              value={key ?? ""}
              displayEmpty
              onChange={(e) => show(i, e.target.value || null)}
              inputProps={{ "aria-label": `Source in slot ${i + 1}` }}
              sx={{ fontSize: "body2.fontSize", minWidth: 120 }}
            >
              <MenuItem value="">(empty)</MenuItem>
              {sources.map((s) => (
                <MenuItem key={s.plan.key} value={s.plan.key}>
                  {s.plan.source} · {s.plan.team}
                </MenuItem>
              ))}
              {panels.map((p) => (
                <MenuItem key={p.key} value={p.key}>
                  {p.title}
                </MenuItem>
              ))}
            </Select>
          );
          const panel = panelOf(key);
          if (panel)
            return (
              <Box
                key={i}
                role="group"
                aria-label={`Slot ${i + 1}`}
                sx={big && i === 0 ? { gridRow: "1 / span 3" } : undefined}
              >
                <Stack
                  direction="row"
                  sx={{ alignItems: "center", gap: 1, mb: 0.5 }}
                >
                  {picker}
                  <Box sx={{ flex: 1 }} />
                  <IconButton
                    size="small"
                    aria-label={maximised === null ? "Maximise" : "Restore"}
                    onClick={() => setMaximised(maximised === null ? i : null)}
                  >
                    {maximised === null ? (
                      <OpenInFullIcon fontSize="small" />
                    ) : (
                      <CloseFullscreenIcon fontSize="small" />
                    )}
                  </IconButton>
                </Stack>
                <Box role="region" aria-label={panel.title}>
                  {panel.node}
                </Box>
              </Box>
            );
          return (
            <Box
              key={i}
              role="group"
              aria-label={`Slot ${i + 1}`}
              sx={big && i === 0 ? { gridRow: "1 / span 3" } : undefined}
              onDragOver={(e) =>
                dragFrom !== null && dragFrom !== i && e.preventDefault()
              }
              onDrop={(e) => {
                e.preventDefault();
                if (dragFrom !== null) show(i, slots[dragFrom] ?? null);
                setDragFrom(null);
              }}
            >
              <Slot
                key={key ?? ""}
                source={source}
                active={source ? active.has(source.plan.key) : false}
                paused={key !== null && !showAll && paused.has(key)}
                now={now}
                maximised={maximised !== null}
                full={key !== null && fullKey === key ? full : null}
                onFull={
                  maximised === null || key === null
                    ? undefined
                    : () => onFull(fullKey === key ? null : key)
                }
                onMaximise={() => setMaximised(maximised === null ? i : null)}
                onDragStart={() => setDragFrom(i)}
                onDragEnd={() => setDragFrom(null)}
                picker={picker}
              />
              {source && (
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "center",
                    gap: 1,
                    mt: 0.5,
                    flexWrap: "wrap",
                  }}
                >
                  {controls(source)}
                </Stack>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

interface View {
  scale: number;
  x: number;
  y: number;
}

const RESET: View = { scale: 1, x: 0, y: 0 };

/** Keeps the zoomed video covering the slot. */
function clampView(v: View, w: number, h: number): View {
  const scale = Math.min(MAX_ZOOM, Math.max(1, v.scale));
  return {
    scale,
    x: Math.min(0, Math.max(w * (1 - scale), v.x)),
    y: Math.min(0, Math.max(h * (1 - scale), v.y)),
  };
}

/** Zooms by `factor` about the point (px, py) of the slot. */
function zoomAt(
  v: View,
  factor: number,
  px: number,
  py: number,
  w: number,
  h: number,
): View {
  const scale = Math.min(MAX_ZOOM, Math.max(1, v.scale * factor));
  const k = scale / v.scale;
  return clampView(
    { scale, x: px - (px - v.x) * k, y: py - (py - v.y) * k },
    w,
    h,
  );
}

function Slot({
  source,
  active,
  paused,
  now,
  maximised,
  full,
  onFull,
  onMaximise,
  onDragStart,
  onDragEnd,
  picker,
}: {
  source: SessionSource | undefined;
  active: boolean;
  paused: boolean;
  now: number;
  maximised: boolean;
  full: FullState | null;
  /** Toggles full resolution; only for a maximised slot. */
  onFull?: () => void;
  onMaximise: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  picker: React.ReactNode;
}) {
  const host = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>(RESET);
  const [showMotion, setShowMotion] = useState(false);
  const chain = source?.chain ?? null;

  useEffect(() => {
    const el = stage.current;
    if (!chain || !el) return;
    chain.attach(el);
    return () => chain.video.remove();
  }, [chain]);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      setView((v) =>
        zoomAt(
          v,
          Math.exp(-e.deltaY * 0.002),
          e.clientX - r.left,
          e.clientY - r.top,
          r.width,
          r.height,
        ),
      );
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const drag = useRef<{ x: number; y: number; view: View } | null>(null);
  const zoomCentre = (factor: number) => {
    const r = host.current?.getBoundingClientRect();
    if (r)
      setView((v) =>
        zoomAt(v, factor, r.width / 2, r.height / 2, r.width, r.height),
      );
  };
  const name = source?.plan.source ?? "empty slot";

  return (
    <Box>
      <Box
        ref={host}
        onPointerDown={(e) => {
          if (view.scale === 1) return;
          drag.current = { x: e.clientX, y: e.clientY, view };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          const r = host.current?.getBoundingClientRect();
          if (!d || !r) return;
          setView(
            clampView(
              {
                ...d.view,
                x: d.view.x + e.clientX - d.x,
                y: d.view.y + e.clientY - d.y,
              },
              r.width,
              r.height,
            ),
          );
        }}
        onPointerUp={() => (drag.current = null)}
        onDoubleClick={() => setView(RESET)}
        sx={{
          position: "relative",
          aspectRatio: "16 / 9",
          bgcolor: source ? "common.black" : undefined,
          border: source ? undefined : "1px dashed",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
          cursor: view.scale > 1 ? "grab" : undefined,
          touchAction: "none",
        }}
      >
        <Box
          ref={stage}
          sx={{ position: "absolute", inset: 0, transformOrigin: "0 0" }}
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          }}
        />
        {source && active && showMotion && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              transformOrigin: "0 0",
              pointerEvents: "none",
            }}
            style={{
              transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            }}
          >
            <MotionOverlay source={source} now={now} />
          </Box>
        )}
        {!source && (
          <Typography
            color="text.secondary"
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
            }}
          >
            Choose a source below
          </Typography>
        )}
        {source && active && paused && (
          <Typography
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              color: "common.white",
              bgcolor: "rgba(0, 0, 0, 0.6)",
              zIndex: 1,
            }}
          >
            Paused for performance
          </Typography>
        )}
        {source && !active && (
          <Typography
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              color: "grey.400",
              bgcolor: "common.black",
              zIndex: 1,
            }}
          >
            No recording
          </Typography>
        )}
        {source && (
          <Stack
            direction="row"
            sx={{
              position: "absolute",
              insetInline: 0,
              // Cameras usually burn their own timestamp in at the top.
              bottom: 0,
              zIndex: 2,
              px: 1,
              py: 0.25,
              gap: 1,
              color: "common.white",
              bgcolor: "rgba(0, 0, 0, 0.55)",
              fontFamily: "monospace",
              fontSize: 12,
              pointerEvents: "none",
            }}
          >
            <span>{source.plan.source}</span>
            <Box component="span" sx={{ flexGrow: 1 }} />
            <ClockConfidence source={source} now={now} />
            <span aria-label="Local time">{localTime(now)}</span>
            <span aria-label="UTC time">{utcTime(now)} UTC</span>
          </Stack>
        )}
      </Box>
      <Stack
        direction="row"
        sx={{ alignItems: "center", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}
      >
        <Box
          draggable={!!source}
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", name);
            onDragStart();
          }}
          onDragEnd={onDragEnd}
          sx={{ cursor: source ? "grab" : undefined }}
          title={source ? "Drag to another slot to swap" : undefined}
        >
          {picker}
        </Box>
        {source && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ [COARSE]: { display: "none" } }}
          >
            {source.plan.team}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {source && (
          <>
            <IconButton
              size="small"
              aria-label={`Zoom out ${name}`}
              onClick={() => zoomCentre(1 / 1.5)}
              disabled={view.scale === 1}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label={`Zoom in ${name}`}
              onClick={() => zoomCentre(1.5)}
              disabled={view.scale >= MAX_ZOOM}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </>
        )}
        {full?.error ? (
          <Chip
            size="small"
            color="error"
            label="Full resolution failed"
            title={full.error}
          />
        ) : (
          full &&
          !full.showing && (
            <Chip size="small" label="Preparing full resolution…" />
          )
        )}
        {source && onFull && (
          <Tooltip
            title={full ? "Back to proxy resolution" : "Full resolution"}
          >
            <IconButton
              size="small"
              aria-label={`Full resolution ${name}`}
              aria-pressed={full !== null}
              onClick={onFull}
            >
              {full ? (
                <HdIcon fontSize="small" color="primary" />
              ) : (
                <HdOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
        {source && (
          <Tooltip title="Boxes round motion found in the picture">
            <IconButton
              size="small"
              aria-label={`Motion boxes ${name}`}
              aria-pressed={showMotion}
              onClick={() => setShowMotion(!showMotion)}
            >
              <HighlightAltIcon
                fontSize="small"
                color={showMotion ? "primary" : undefined}
              />
            </IconButton>
          </Tooltip>
        )}
        {source && <SnapshotButton source={source} now={now} name={name} />}
        <Tooltip title={maximised ? "Back to the grid" : "Maximise"}>
          <IconButton
            size="small"
            aria-label={maximised ? `Restore ${name}` : `Maximise ${name}`}
            onClick={onMaximise}
          >
            {maximised ? (
              <CloseFullscreenIcon fontSize="small" />
            ) : (
              <OpenInFullIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

const CONFIDENCE_COLOUR: Record<string, string> = {
  High: "success.light",
  Medium: "warning.light",
  Low: "error.light",
};

/** How far the start time of the file at the playhead can be trusted (design §6.4). */
function ClockConfidence({
  source,
  now,
}: {
  source: SessionSource;
  now: number;
}) {
  const file = source.plan.video[fileAt(source.plan.video, now)];
  const confidence = file?.timeConfidence;
  if (!file || file.fromUtcMs > now || !confidence) return null;
  return (
    <Box
      component="span"
      aria-label={`Clock confidence ${confidence}`}
      title={file.timeBasis ? `Start time from ${file.timeBasis}` : undefined}
      sx={{ color: CONFIDENCE_COLOUR[confidence] ?? "inherit" }}
    >
      clock {confidence.toLowerCase()}
    </Box>
  );
}

/**
 * Exports the frame at the playhead, full size from the original, rendered
 * and audited by the server (design §7.3).
 */
/** Boxes round the motion the analysis found in the frame at the playhead (design §8.2). */
function MotionOverlay({
  source,
  now,
}: {
  source: SessionSource;
  now: number;
}) {
  const file = source.plan.video[fileAt(source.plan.video, now)];
  const motion = useQuery({
    queryKey: ["motion", file?.id],
    queryFn: () =>
      unwrap(
        api.GET("/api/files/{id}/motion", {
          params: { path: { id: file?.id ?? "" } },
        }),
      ),
    enabled: !!file,
    staleTime: Infinity,
    retry: false,
  });
  const message = (text: string) => (
    <Typography
      variant="caption"
      sx={{
        position: "absolute",
        top: 4,
        left: 4,
        px: 0.5,
        color: "common.white",
        bgcolor: "rgba(0, 0, 0, 0.55)",
      }}
    >
      {text}
    </Typography>
  );
  if (!file || now < file.fromUtcMs || now >= file.toUtcMs) return null;
  if (motion.isError) return message("Motion not analysed yet");
  if (!motion.data) return null;
  if (motion.data.boxes.length === 0 && motion.data.motion.length > 0)
    return message("Analysed before motion boxes: run the analysis again");
  const v = source.chain?.video;
  const [ox, oy, cw, ch] = contained(
    v?.videoWidth ?? 0,
    v?.videoHeight ?? 0,
    v?.clientWidth ?? 0,
    v?.clientHeight ?? 0,
  );
  return (
    <svg
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      role="img"
      aria-label="Motion boxes"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
    >
      {boxesAt(motion.data, now - file.fromUtcMs).map(([x, y, w, h], i) => (
        <rect
          key={i}
          data-testid="motion-box"
          x={ox + (x ?? 0) * cw}
          y={oy + (y ?? 0) * ch}
          width={(w ?? 0) * cw}
          height={(h ?? 0) * ch}
          fill="none"
          stroke="#ffeb3b"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
}

function SnapshotButton({
  source,
  now,
  name,
}: {
  source: SessionSource;
  now: number;
  name: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const file = source.plan.video[fileAt(source.plan.video, now)];
  const recording = file !== undefined && file.fromUtcMs <= now;

  const take = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const { data, error, response } = await api.POST(
        "/api/files/{id}/snapshot",
        {
          params: { path: { id: file.id } },
          body: { at_utc: new Date(now).toISOString() },
          parseAs: "blob",
        },
      );
      if (!data)
        throw new Error(
          (error as { message?: string } | undefined)?.message ??
            `HTTP ${response.status}`,
        );
      const filename =
        /filename="([^"]+)"/.exec(
          response.headers.get("content-disposition") ?? "",
        )?.[1] ?? "snapshot.png";
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tooltip
      title={
        error ??
        "Snapshot: the frame at the playhead, full size from the original (audited)"
      }
    >
      <span>
        <IconButton
          size="small"
          aria-label={`Snapshot ${name}`}
          onClick={() => void take()}
          disabled={!recording || busy}
          color={error ? "error" : "default"}
        >
          <PhotoCameraIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}
